import { uploadImage } from '@/firebase';
import { formatUTCDate, createDownloadableLink } from '@/helpers';
import { FORMAT_DD_MM_YYYY } from '@/module/company/constants';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { synthesize } from 'redux-dispatcher';

const mapDispatchToAC = {
  getDataSuccess: (data) => ({ data }),
  getData:
    (search, paging, filterOption = {}, sortOption = {}, patientId) =>
    async ({ Api, getState }) => {
      let pagingData = paging || getState().upcomingHS.paging;
      const payload = {
        pageIndex: pagingData.pageIndex,
        pageSize: pagingData.pageSize,
        search,
        ...filterOption,
        startDate: filterOption?.startDate
          ? formatUTCDate(filterOption?.startDate, { isStarOfDay: true })
          : undefined,
        endDate: filterOption?.endDate
          ? formatUTCDate(filterOption?.endDate, { isEndOfDay: true })
          : undefined,
        ...sortOption,
        patientId,
      };

      let { result, status } = await Api.post(
        `patient-appointments/filter-paxscreenings`,
        payload
      );

      status === 200 && upcomingHSDispatcher.getDataSuccess(result);
    },
  resetData: () => ({}),
  getConsentsByPatientID:
    (id, callback) =>
    async ({ Api }) => {
      const { result, status } = await Api.get(
        `pax-consents/by-pax-screening/${id}`
      );
      status === 200 && callback && callback(result);
    },
  getConsentsByServices:
    (data, callback) =>
    async ({ Api }) => {
      const { result, status } = await Api.post(
        `consents/get-test-consents`,
        data
      );
      status === 200 && callback && callback(result);
    },
  cancelTeleAppointment:
    (id, callback) =>
    async ({ Api }) => {
      const { status } = await Api.put(`pax-screenings/${id}/cancellation`);
      status === 200 && callback && callback();
    },
  setState: (state, value) => ({ state, value }),
  signConsentForm:
    (id, data, callback) =>
    async ({ Api, customToast }) => {
      upcomingHSDispatcher.setState('loading', true);

      let { staffSignatureUrl } = data;
      if (data.staffSignatureUrl?.constructor === File) {
        staffSignatureUrl = await uploadImage(data.staffSignatureUrl);
      }

      const { status } = await Api.put(`pax-consents/sign/${id}`, {
        ...data,
        staffSignatureUrl,
      });
      upcomingHSDispatcher.setState('loading', false);

      if (status === 200) {
        customToast('success', 'Sign Consent Form Success');
        callback && callback();
      }
    },
  getExportExcel:
    (params) =>
    async ({ Api }) => {
      let { result, status } = await Api.post(
        `patient-appointments/exports/csv`,
        {
          ...params,
          byHealthScreening: !params.userId,
          startDate: params?.startDate
            ? formatUTCDate(params?.startDate, { isStarOfDay: true })
            : undefined,
          endDate: params?.endDate
            ? formatUTCDate(params?.endDate, { isEndOfDay: true })
            : undefined,
          createdStartDate: params?.createdStartDate
            ? formatUTCDate(params?.createdStartDate, { isStarOfDay: true })
            : undefined,
          createdEndDate: params?.createdEndDate
            ? formatUTCDate(params?.createdEndDate, { isEndOfDay: true })
            : undefined,
        }
      );
      status === 200 && createDownloadableLink(result, 'Result');
    },
  editTeleAppointment:
    (params, cb) =>
    async ({ Api }) => {
      const {
        id,
        timeSlotId,
        date,
        isSuppression,
        healthScreeningId,
        code,
        isHomebased,
        homebasedAddressId,
        homebasedLocationId,
      } = params;
      let { status } = await Api.put(
        `patient-appointments/${id}/pax-screening`,
        {
          timeSlotId,
          date: moment(date).format('YYYY-MM-DD'),
          isSuppression,
          healthScreeningId,
          code: isEmpty(code) ? null : code,
          isHomebased,
          homebasedAddressId,
          homebasedLocationId,
        }
      );
      if (status === 200) {
        cb && cb({ isSuccess: true });
      } else {
        cb && cb({ isSuccess: false });
      }
    },
  downloadReportByStatus:
    (id, shortId) =>
    async ({ Api }) => {
      const response = await Api.post(
        `pax-screenings/${id}/export-profile-statement`,
        {}
      );

      const { status, result } = response;

      const fileName = `${shortId}/hs-appointment/${Date.now()}`;

      status === 200 && createDownloadableLink(result, fileName, 'pdf');
    },
  getPaxScreening:
    (id, onSuccess, onFail) =>
    async ({ Api }) => {
      const { status, result } = await Api.get(
        `analysis-pax-screenings/${id}/report`
      );
      status === 200 ? onSuccess(result) : onFail();
    },
  getPaxScreeningWithToken:
    (id, accessToken, onSuccess, onFail) =>
    async ({ Api }) => {
      if (accessToken) {
        const { status, result } = await Api.get(
          `analysis-pax-screenings/${id}/report`,
          undefined,
          { Authorization: `Bearer ${accessToken}` }
        );

        status === 200 ? onSuccess(result) : onFail();
      }
    },
  createAppointment:
    (params, callback) =>
    async ({ Api }) => {
      const response = await Api.post(`pax-screenings/staff-register`, {
        ...params,
      });

      const { status } = response;
      if (status === 200) {
        callback && callback({ isSuccess: true });
      } else {
        callback && callback({ isSuccess: false });
      }
    },
  patientCheckInLocation:
    (id, callback) =>
    async ({ Api }) => {
      const { status } = await Api.put(
        `pax-screenings/${id}/check-in-location`
      );
      if (status === 200) {
        callback && callback();
      }
    },
  followUpAppointment:
    (id, params, callback) =>
    async ({ Api }) => {
      const response = await Api.post(`pax-screenings/${id}/follow-up-appt`, {
        ...params,
      });

      const { status } = response;
      if (status === 200) {
        callback && callback({ isSuccess: true });
      } else {
        callback && callback({ isSuccess: false });
      }
    },
  downloadIRReport:
    (id, callback) =>
    async ({ Api, customToast }) => {
      const { status, result } = await Api.get(
        `analysis-pax-screenings/${id}/ir-report`
      );
      if (status === 200) {
        customToast('success', 'Download success');
        createDownloadableLink(result, 'Health Screening Report', 'pdf');
      }
    },
  getLinkDownloadIRReport:
    (id, callback) =>
    async ({ Api }) => {
      const { status, result } = await Api.get(
        `analysis-pax-screenings/${id}/ir-report`
      );
      if (status === 200) callback && callback(result);
    },
  generateIRReport:
    (id, token, paxScreening, callback) =>
    async ({ Api }) => {
      const jsonData = JSON.stringify({
        token,
        paxScreening: {
          id,
          ...paxScreening,
        },
      });
      const { status } = await Api.post(
        `analysis-pax-screenings/generate-ir-report`,
        {
          jsonData,
        }
      );
      if (status === 200) {
        callback && callback();
      }
    },
  regenerateIRReport:
    (id) =>
    async ({ Api, customToast }) => {
      const { status } = await Api.put(
        `analysis-pax-screenings/${id}/ir-report/regenerations`
      );
      if (status === 200) {
        customToast('success', 'Regenerate success');
      }
    },
  revertAppoinmentToUpcoming:
    (id, callback) =>
    async ({ Api, customToast }) => {
      const { status } = await Api.put(`pax-screenings/${id}/revert`);
      if (status === 200) {
        callback && callback();
      }
    },

  doneAllConsents:
    (id, callback) =>
    async ({ Api, customToast }) => {
      const { status } = await Api.put(
        `pax-screenings/${id}/done-all-consents`
      );
      if (status === 200) {
        customToast('success', 'Update all consents success');
        callback && callback();
      }
    },
};

const upcomingHSDispatcher = synthesize('upcomingHS', mapDispatchToAC);

export default upcomingHSDispatcher;
